import { useState, useEffect, useCallback, useMemo } from 'react'
import { warnAfterSave } from '../api/configurationCheck'
import {
  leadRoutingRulesApi,
  type LeadRoutingMode,
} from '../api/leadRoutingRules'
import {
  leadRoutingApi,
  parseConditions,
  serializeConditions,
  splitInValue,
  type LeadRoutingField,
  type LeadRoutingRuleBodyExt,
  type LeadRoutingRuleExt,
  type LeadRoutingVocabulary,
  type RoutingCondition,
} from '../api/leadRouting'
import { agentsApi, type AgentProfile } from '../api/team'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  useSnackbar,
  useConfirm,
  FormGrid,
  FormSection,
} from '../components/ui'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'

interface RuleFormState {
  name: string
  priority: number
  targetAgentId: number | ''
  isActive: boolean
  /** F-116: condizioni guidate (campo, operatore, valore). */
  conditions: RoutingCondition[]
  /** F-116: salta la regola se l'agente non e' attivo, assente o fuori orario. */
  skipWhenTargetUnavailable: boolean
  mode: LeadRoutingMode
  members: MemberFormRow[]
}

interface MemberFormRow {
  userId: number | ''
  weight: number
  maxOpenLeads: number | ''
  /** F-088: settori abilitati, separati da virgola; vuoto = tutti. */
  sectors: string
  /** F-116: province ammesse (sigle); vuoto = tutte. */
  areaProvinces: string[]
  /** F-116: regioni ammesse; vuoto = tutte. */
  areaRegions: string[]
  /** F-116: soglia di performance in percentuale (0-100); vuoto = ignorata. */
  minPct: number | ''
}

const MODE_LABEL: Record<LeadRoutingMode, string> = {
  single: 'Agente singolo',
  round_robin: 'Round robin',
  weighted: 'Ponderata',
}

const EMPTY_FORM: RuleFormState = {
  name: '',
  priority: 0,
  targetAgentId: '',
  isActive: true,
  conditions: [],
  skipWhenTargetUnavailable: false,
  mode: 'single',
  members: [],
}

// F-116: costruttore guidato delle condizioni. Il vocabolario (campi, operatori, valori) arriva dal backend
// (GET api/lead-routing-rules/vocabulary): lo stesso elenco valida le regole in scrittura.
const OPERATOR_FALLBACK_LABEL: Record<string, string> = {
  eq: 'è uguale a',
  ne: 'è diverso da',
  in: 'è uno tra',
  contains: 'contiene',
  startsWith: 'inizia con',
  gt: 'maggiore di',
  gte: 'maggiore o uguale a',
  lt: 'minore di',
  lte: 'minore o uguale a',
}

function operatorLabel(vocab: LeadRoutingVocabulary | null, op: string): string {
  return vocab?.operators.find(o => o.key === op)?.label ?? OPERATOR_FALLBACK_LABEL[op] ?? op
}

/** Riga del costruttore: campo, operatore e valore (a scelta singola, multipla o libero secondo il campo). */
function ConditionRow({
  condition, vocab, onChange, onRemove,
}: {
  condition: RoutingCondition
  vocab: LeadRoutingVocabulary | null
  onChange: (c: RoutingCondition) => void
  onRemove: () => void
}) {
  const fields = vocab?.fields ?? []
  const field: LeadRoutingField | undefined = fields.find(f => f.key === condition.field)
  const operators = field?.operators ?? [condition.operator]
  const options = field?.options ?? null
  const isIn = condition.operator === 'in'

  function changeField(key: string) {
    const next = fields.find(f => f.key === key)
    const op = next?.operators.includes(condition.operator) ? condition.operator : (next?.operators[0] ?? 'eq')
    onChange({ field: key, operator: op, value: '' })
  }

  return (
    <FormGrid>
      <Select
        label="Campo"
        labelHidden
        value={condition.field}
        onChange={e => changeField(e.target.value)}
      >
        <option value="">Campo…</option>
        {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        {condition.field !== '' && !field && <option value={condition.field}>{condition.field} (sconosciuto)</option>}
      </Select>
      <Select
        label="Operatore"
        labelHidden
        value={condition.operator}
        onChange={e => onChange({ ...condition, operator: e.target.value, value: '' })}
      >
        {operators.map(o => <option key={o} value={o}>{operatorLabel(vocab, o)}</option>)}
      </Select>
      <div className="md-span-full d-flex gap-2 align-items-start">
        {options && isIn ? (
          <Select
            className="flex-grow-1"
            multiple
            size={Math.min(6, Math.max(3, options.length))}
            label="Valori"
            labelHidden
            supportingText="Tieni premuto Ctrl (o Cmd) per scegliere più valori."
            value={splitInValue(condition.value)}
            onChange={e => onChange({
              ...condition,
              value: Array.from(e.target.selectedOptions).map(o => o.value).join(','),
            })}
          >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        ) : options ? (
          <Select
            className="flex-grow-1"
            label="Valore"
            labelHidden
            value={condition.value}
            onChange={e => onChange({ ...condition, value: e.target.value })}
          >
            <option value="">Valore…</option>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        ) : (
          <TextField
            className="flex-grow-1"
            label="Valore"
            labelHidden
            type={field?.type === 'number' && !isIn ? 'number' : 'text'}
            placeholder={isIn ? 'Valori separati da virgola' : field?.key === 'postalCode' ? 'Es. 20' : 'Valore'}
            value={condition.value}
            onChange={e => onChange({ ...condition, value: e.target.value })}
          />
        )}
        <IconButton icon="close" size="sm" aria-label="Rimuovi condizione" onClick={onRemove} />
      </div>
    </FormGrid>
  )
}

export function LeadRoutingRulesPage() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<LeadRoutingRuleExt[]>([])
  const [vocab, setVocab] = useState<LeadRoutingVocabulary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [agents, setAgents] = useState<AgentProfile[]>([])

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await leadRoutingApi.list()
      setItems(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // F-116: il vocabolario delle condizioni (campi, operatori, province, regioni).
  useEffect(() => {
    leadRoutingApi.vocabulary().then(setVocab).catch(() => setVocab(null))
  }, [])

  // F-096: dopo "Clona" la copia (inattiva) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(items, r => openEdit(r))

  // TASK-0148: target-agent dropdown, sourced from `GET /api/agents` via the `agentsApi` module
  // that already exists in api/team.ts (TASK-0072) — no second agents API module needed. Only
  // active agents make sense as a routing destination. `agentsApi.list()` caps at its own
  // hardcoded page size (25, see AgentsController's default) which this minimal selector accepts
  // rather than reaching into team.ts (out of this task's allowed_paths) to make it overridable.
  useEffect(() => {
    agentsApi.list({ isActive: true }).then(res => setAgents(res.items)).catch(() => setAgents([]))
  }, [])

  // LeadRoutingRule.TargetAgentId is a user id (written straight to Lead.AssignedAgentId — see
  // LeadRoutingRule.cs), not an AgentProfile id, so the lookup keys off `userId`. AgentProfile
  // carries no display name (that lives in the Identity module, which the Leads/Settings modules
  // deliberately don't reference), so the label falls back to the agent's `code` when set, same
  // convention TeamPage.tsx already uses for an agent with no joined user record.
  const agentLabel = useMemo(() => {
    const map = new Map<number, string>()
    agents.forEach(a => map.set(a.userId, a.code ? `${a.code} — Utente #${a.userId}` : `Utente #${a.userId}`))
    return (userId: number) => map.get(userId) ?? `Utente #${userId}`
  }, [agents])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSaveError('')
    setShowModal(true)
  }

  function openEdit(rule: LeadRoutingRuleExt) {
    setForm({
      name: rule.name,
      priority: rule.priority,
      targetAgentId: rule.targetAgentId,
      isActive: rule.isActive,
      conditions: parseConditions(rule.conditions) ?? [],
      skipWhenTargetUnavailable: rule.skipWhenTargetUnavailable ?? false,
      mode: rule.mode ?? 'single',
      members: (rule.members ?? []).map(m => ({
        userId: m.userId,
        weight: m.weight,
        maxOpenLeads: m.maxOpenLeads ?? '',
        sectors: (m.enabledSectors ?? []).join(', '),
        areaProvinces: m.areaProvinces ?? [],
        areaRegions: m.areaRegions ?? [],
        minPct: m.minConversionRate == null ? '' : Math.round(m.minConversionRate * 10000) / 100,
      })),
    })
    setEditId(rule.id)
    setSaveError('')
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setSaveError('')
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError('Il nome è obbligatorio')
      return
    }
    if (form.targetAgentId === '') {
      setSaveError('Seleziona un agente target')
      return
    }
    if (form.conditions.some(c => c.field === '' || c.value.trim() === '')) {
      setSaveError('Completa o rimuovi le condizioni: ognuna vuole campo, operatore e valore. Senza condizioni la regola si applica a ogni lead.')
      return
    }
    const conditionsJson = serializeConditions(form.conditions)

    if (form.mode !== 'single') {
      if (form.members.length === 0) {
        setSaveError('Aggiungi almeno un membro al gruppo')
        return
      }
      const ids = form.members.map(m => m.userId)
      if (ids.some(i => i === '')) {
        setSaveError('Seleziona un utente per ogni membro')
        return
      }
      if (new Set(ids).size !== ids.length) {
        setSaveError('Un utente può comparire una sola volta nel gruppo')
        return
      }
      if (form.mode === 'weighted' && form.members.some(m => !(m.weight > 0))) {
        setSaveError('Nella distribuzione ponderata ogni peso deve essere maggiore di zero')
        return
      }
      if (form.members.some(m => m.maxOpenLeads !== '' && !(m.maxOpenLeads > 0))) {
        setSaveError('Il tetto di lead aperti, se indicato, deve essere maggiore di zero')
        return
      }
      if (form.members.some(m => m.minPct !== '' && !(m.minPct >= 0 && m.minPct <= 100))) {
        setSaveError('La performance minima, se indicata, deve essere tra 0 e 100')
        return
      }
    }

    setSaving(true)
    setSaveError('')
    try {
      const payload: LeadRoutingRuleBodyExt = {
        name: form.name,
        conditions: conditionsJson,
        skipWhenTargetUnavailable: form.skipWhenTargetUnavailable,
        targetAgentId: Number(form.targetAgentId),
        priority: form.priority,
        isActive: form.isActive,
        mode: form.mode,
        members: form.mode === 'single' ? [] : form.members.map(m => ({
          userId: Number(m.userId),
          weight: form.mode === 'weighted' ? m.weight : null,
          maxOpenLeads: m.maxOpenLeads === '' ? null : m.maxOpenLeads,
          enabledSectors: m.sectors.split(',').map(x => x.trim()).filter(x => x.length > 0),
          areaProvinces: m.areaProvinces,
          areaRegions: m.areaRegions,
          minConversionRate: m.minPct === '' ? null : m.minPct / 100,
        })),
      }
      let savedId = editId
      if (editId !== null) {
        await leadRoutingApi.update(editId, payload)
      } else {
        const created = await leadRoutingApi.create(payload)
        savedId = (created as { id?: number } | null)?.id ?? null
      }
      closeModal()
      void load()
      // F-122: una regola salvata come attiva si controlla subito; e' un avviso, la modifica e' gia' salvata.
      if (payload.isActive && savedId !== null) void warnAfterSave('LeadRoutingRule', savedId, form.name)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(rule: LeadRoutingRuleExt) {
    if (!(await askConfirm({ title: 'Elimina regola', message: `Eliminare la regola "${rule.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await leadRoutingRulesApi.delete(rule.id)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : "Errore durante l'eliminazione", tone: 'error' })
    }
  }

  return (
    <Page>
      <PageHeader
        title="Instradamento lead"
        icon="alt_route"
        count={items.length > 0 ? items.length : undefined}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuova regola
          </Button>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="alt_route" title="Nessuna regola di instradamento trovata" />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>#</th>
                <th>Nome</th>
                <th>Priorità</th>
                <th>Modalità</th>
                <th>Agente target</th>
                <th>Stato</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(rule => (
                <tr key={rule.id}>
                  <td className="text-muted md-num">{rule.id}</td>
                  <td className="md-typescale-emphasized-body-medium">{rule.name}</td>
                  <td className="text-muted md-num">{rule.priority}</td>
                  <td>
                    {MODE_LABEL[rule.mode ?? 'single']}
                    {(rule.members?.length ?? 0) > 0 && (
                      <span className="text-muted md-typescale-body-small"> · {rule.members.length} membri</span>
                    )}
                  </td>
                  <td>
                    {agentLabel(rule.targetAgentId)}
                    {rule.mode !== 'single' && <span className="text-muted md-typescale-body-small"> (riserva)</span>}
                  </td>
                  <td>
                    {rule.isActive ? (
                      <StatusPill tone="success">Attiva</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Inattiva</StatusPill>
                    )}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <CloneConfigurationButton
                        entityLabel="regola di instradamento"
                        url={`/api/lead-routing-rules/${rule.id}/clone`}
                        defaultName={rule.name}
                        note="Si copiano condizioni, modalità e membri. La copia nasce inattiva."
                        onCloned={async id => { await load(); openAfterClone(id) }}
                      />
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(rule)} />
                      <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void handleDelete(rule)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <RuleLifecyclePanel family="lead-routing" onChanged={() => void load()} />

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica regola' : 'Nuova regola'}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {saveError && <Callout tone="error">{saveError}</Callout>}
          <FormSection title="Regola">
            <TextField
              className="md-span-full"
              label="Nome *"
              type="text"
              supportingText="Es. Lead Lombardia Energia → Rossi"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <Select
              label="Agente target *"
              value={form.targetAgentId}
              onChange={e => {
                const val = e.target.value
                setForm(f => ({ ...f, targetAgentId: val ? parseInt(val, 10) : '' }))
              }}
            >
              <option value="">Seleziona un agente…</option>
              {agents.map(a => (
                <option key={a.id} value={a.userId}>
                  {a.code ? `${a.code} — Utente #${a.userId}` : `Utente #${a.userId}`}
                </option>
              ))}
            </Select>
            <TextField
              label="Priorità"
              type="number"
              supportingText="Più alta vince: a parità di condizioni soddisfatte, la priorità più alta viene applicata per prima."
              value={form.priority}
              onChange={e =>
                setForm(f => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))
              }
            />
          </FormSection>
          <FormSection title="Instradamento" columns={1}>
            <Select
              label="Distribuzione"
              value={form.mode}
              onChange={e => setForm(f => ({ ...f, mode: e.target.value as LeadRoutingMode }))}
              supportingText={form.mode !== 'single'
                ? "L'agente target resta obbligatorio: riceve il lead come riserva quando nessun membro è idoneo (utente disattivato o oltre il tetto di lead aperti). Il contatore delle assegnazioni riparte da quello salvato, non si azzera modificando la regola."
                : undefined}
            >
              <option value="single">Agente singolo (sempre l'agente target)</option>
              <option value="round_robin">Round robin (a turno tra i membri)</option>
              <option value="weighted">Ponderata (in proporzione ai pesi)</option>
            </Select>
          </FormSection>
          {form.mode !== 'single' && (
            <FormSection title="Membri del gruppo" columns={1}>
              {form.members.length === 0 && (
                <div className="text-muted md-typescale-body-small">Nessun membro: aggiungine almeno uno.</div>
              )}
              {form.members.map((m, idx) => (
                <Card as="div" variant="filled" padding="md" key={idx}>
                  <FormGrid>
                    <div className="md-span-full d-flex gap-2 align-items-start">
                      <Select
                        className="flex-grow-1"
                        label="Utente"
                        labelHidden
                        value={m.userId}
                        onChange={e => {
                          const val = e.target.value ? parseInt(e.target.value, 10) : ''
                          setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, userId: val } : x) }))
                        }}
                      >
                        <option value="">Utente…</option>
                        {agents.map(a => (
                          <option key={a.id} value={a.userId}>
                            {a.code ? `${a.code} — Utente #${a.userId}` : `Utente #${a.userId}`}
                          </option>
                        ))}
                        {m.userId !== '' && !agents.some(a => a.userId === m.userId) && (
                          <option value={m.userId}>Utente #{m.userId}</option>
                        )}
                      </Select>
                      <IconButton
                        icon="close"
                        size="sm"
                        aria-label="Rimuovi membro"
                        onClick={() => setForm(f => ({ ...f, members: f.members.filter((_, i) => i !== idx) }))}
                      />
                    </div>
                    <TextField
                      type="number"
                      min={1}
                      label="Peso"
                      labelHidden
                      title="Peso"
                      placeholder="Peso"
                      disabled={form.mode === 'round_robin'}
                      value={form.mode === 'round_robin' ? 1 : m.weight}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10) || 0
                        setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, weight: val } : x) }))
                      }}
                    />
                    <TextField
                      type="number"
                      min={1}
                      label="Tetto lead"
                      labelHidden
                      title="Tetto di lead aperti (vuoto = nessun tetto)"
                      placeholder="Tetto lead"
                      value={m.maxOpenLeads}
                      onChange={e => {
                        const val = e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0
                        setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, maxOpenLeads: val } : x) }))
                      }}
                    />
                    <TextField
                      className="md-span-full"
                      label="Settori abilitati"
                      labelHidden
                      title="Settori abilitati, separati da virgola (vuoto = tutti i settori)"
                      placeholder="Settori abilitati (vuoto = tutti), es. Energia, Telefonia"
                      value={m.sectors}
                      onChange={e => {
                        const val = e.target.value
                        setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, sectors: val } : x) }))
                      }}
                    />
                    <Select
                      multiple
                      size={4}
                      label="Province ammesse"
                      title="Province ammesse (nessuna = tutte). Ctrl/Cmd per più valori."
                      value={m.areaProvinces}
                      onChange={e => {
                        const val = Array.from(e.target.selectedOptions).map(o => o.value)
                        setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, areaProvinces: val } : x) }))
                      }}
                    >
                      {(vocab?.provinces ?? []).map(p => (
                        <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                      ))}
                    </Select>
                    <Select
                      multiple
                      size={4}
                      label="Regioni ammesse"
                      title="Regioni ammesse (nessuna = tutte). Ctrl/Cmd per più valori."
                      value={m.areaRegions}
                      onChange={e => {
                        const val = Array.from(e.target.selectedOptions).map(o => o.value)
                        setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, areaRegions: val } : x) }))
                      }}
                    >
                      {(vocab?.regions ?? []).map(r => <option key={r} value={r}>{r}</option>)}
                    </Select>
                    <TextField
                      className="md-span-full"
                      type="number"
                      min={0}
                      max={100}
                      label="Performance minima (%)"
                      title="Convertiti su assegnati negli ultimi 30 giorni (vuoto = ignorata)"
                      supportingText="Vuoto = ignorata"
                      value={m.minPct}
                      onChange={e => {
                        const val = e.target.value === '' ? '' : Number(e.target.value)
                        setForm(f => ({ ...f, members: f.members.map((x, i) => i === idx ? { ...x, minPct: val } : x) }))
                      }}
                    />
                  </FormGrid>
                </Card>
              ))}
              <div>
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() =>
                    setForm(f => ({ ...f, members: [...f.members, { userId: '', weight: 1, maxOpenLeads: '', sectors: '', areaProvinces: [], areaRegions: [], minPct: '' }] }))
                  }
                >
                  Aggiungi membro
                </Button>
              </div>
              <div className="md-typescale-body-small text-muted">
                Peso: quota relativa (solo ponderata). Tetto: numero massimo di lead aperti
                (non convertiti né persi) che il membro può avere; vuoto = nessun tetto.
                La distribuzione vale per i lead creati con questa regola, non per l'import CSV.
                Settori: se indicati, il membro riceve solo lead di quei settori (un lead senza settore va
                solo a chi li abilita tutti). Il membro in ferie o fuori orario (Impostazioni, Call center)
                non viene scelto; senza orari configurati è sempre disponibile.
                Area: se indicate, il membro riceve solo lead di quelle province o regioni (un lead senza
                provincia va solo a chi non ha un'area). Performance minima: quota di lead convertiti sugli
                assegnati negli ultimi 30 giorni; si applica solo se il membro ha almeno un lead assegnato nel periodo.
              </div>
            </FormSection>
          )}
          <FormSection title="Condizioni" columns={1}>
            {form.conditions.length === 0 && (
              <Callout tone="warning">
                Nessuna condizione: la regola si applica a <strong>ogni</strong> lead. Con priorità alta
                assorbe tutti i lead; usala come regola di riserva a priorità bassa.
              </Callout>
            )}
            {form.conditions.map((c, idx) => (
              <ConditionRow
                key={idx}
                condition={c}
                vocab={vocab}
                onChange={next => setForm(f => ({ ...f, conditions: f.conditions.map((x, i) => i === idx ? next : x) }))}
                onRemove={() => setForm(f => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }))}
              />
            ))}
            <div>
              <Button
                variant="outlined"
                size="sm"
                disabled={!vocab}
                onClick={() => setForm(f => ({ ...f, conditions: [...f.conditions, { field: '', operator: 'eq', value: '' }] }))}
              >
                Aggiungi condizione
              </Button>
            </div>
            <div className="md-typescale-body-small text-muted">
              Devono essere tutte vere perché la regola scatti. Il territorio si ricava dalla provincia del lead
              (regione compresa); ora e giorno d'arrivo sono quelli di Roma. L'instradamento non vale per l'import CSV.
            </div>
            <div className="md-typescale-label-medium text-muted">Anteprima JSON (sola lettura)</div>
            <pre
              className="font-monospace md-typescale-body-small mb-0 p-2 rounded md-u-pre-wrap md-u-code-box"
            >
              {JSON.stringify(
                form.conditions.filter(c => c.field !== '').map(c => ({ field: c.field, operator: c.operator, value: c.value })),
                null, 2,
              )}
            </pre>
          </FormSection>
          <FormSection title="Opzioni" columns={1}>
            <Checkbox
              id="lrr-isActive"
              label="Attiva"
              checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
            />
            <div>
              <Checkbox
                id="lrr-skip"
                label="Salta la regola se l'agente non è disponibile"
                checked={form.skipWhenTargetUnavailable}
                onChange={e => setForm(f => ({ ...f, skipWhenTargetUnavailable: e.target.checked }))}
              />
              <div className="md-typescale-body-small text-muted">
                Se l'agente (o la riserva del gruppo) è disattivato, in ferie o fuori orario, il lead passa alla
                regola successiva invece di essere assegnato a lui.
              </div>
            </div>
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
